import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Owns all admin dashboard data fetching + CRUD handlers.
// Modal-closing stays in the component via optional onDone callbacks.
export function useAdminData() {
  const [stats, setStats] = useState({});
  const [bookings, setBookings] = useState([]);
  const [cars, setCars] = useState([]);
  const [messages, setMessages] = useState([]);
  const [syncEvents, setSyncEvents] = useState([]);
  const [webhook, setWebhook] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [hostApplications, setHostApplications] = useState([]);
  const [payouts, setPayouts] = useState({ hosts: [], bookings: [] });
  const [claims, setClaims] = useState([]);
  const [hosts, setHosts] = useState({ hosts: [], totals: {} });

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/cars").then((r) => setCars(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/admin/turo/events").then((r) => setSyncEvents(r.data)).catch(() => {});
    api.get("/admin/turo/webhook-info").then((r) => setWebhook(r.data)).catch(() => {});
    api.get("/admin/discounts").then((r) => setDiscounts(r.data)).catch(() => {});
    api.get("/admin/host-applications").then((r) => setHostApplications(r.data)).catch(() => {});
    api.get("/admin/payouts").then((r) => setPayouts(r.data)).catch(() => {});
    api.get("/admin/claims").then((r) => setClaims(r.data)).catch(() => {});
    api.get("/admin/hosts").then((r) => setHosts(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const fail = (e) => toast.error(formatApiError(e.response?.data?.detail));

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (e) { fail(e); }
  };

  const resendReceipt = async (id) => {
    try {
      const res = await api.post(`/admin/bookings/${id}/resend-receipt`);
      if (res.data.sent) toast.success(`Receipt emailed to ${res.data.email}`);
      else toast.warning(res.data.message);
    } catch (e) { fail(e); }
  };

  const markOfflinePaid = async (id, method) => {
    try {
      await api.post(`/admin/bookings/${id}/offline-payment`, { method });
      toast.success("Offline payment recorded");
      load();
    } catch (e) { fail(e); }
  };

  const stripeTransfer = async (id) => {
    if (!window.confirm("Send this host's payout to their connected bank via Stripe ACH?")) return;
    try {
      const res = await api.post(`/admin/payouts/${id}/transfer`);
      toast.success(`Payout of $${res.data.amount} sent via Stripe ACH`);
      load();
    } catch (e) { fail(e); }
  };

  const saveCar = async (data, onDone) => {
    try {
      const payload = {
        ...data, weekly_rate: Number(data.weekly_rate), deposit: Number(data.deposit), seats: Number(data.seats),
        year: data.year === "" || data.year == null ? null : Number(data.year),
        odometer: data.odometer === "" || data.odometer == null ? null : Number(data.odometer),
        features: typeof data.features === "string" ? data.features.split(",").map((f) => f.trim()).filter(Boolean) : data.features,
        images: typeof data.images === "string" ? data.images.split("\n").map((s) => s.trim()).filter(Boolean) : (data.images || []),
      };
      if (data.id) await api.put(`/admin/cars/${data.id}`, payload);
      else await api.post("/admin/cars", payload);
      toast.success("Car saved");
      onDone?.();
      load();
    } catch (e) { fail(e); }
  };

  const deleteCar = async (id) => {
    if (!window.confirm("Delete this car?")) return;
    try {
      await api.delete(`/admin/cars/${id}`);
      toast.success("Car deleted");
      load();
    } catch (e) { fail(e); }
  };

  const toggleAvailability = async (car) => {
    const next = !(car.available !== false);
    try {
      await api.put(`/admin/cars/${car.id}/availability`, { available: next });
      toast.success(next ? "Marked available" : "Marked unavailable");
      load();
    } catch (e) { fail(e); }
  };

  const reviewCar = async (id, status, reason = "") => {
    try {
      await api.put(`/admin/cars/${id}/review`, { status, reason });
      toast.success(status === "approved" ? "Vehicle approved & published" : "Vehicle rejected");
      load();
    } catch (e) { fail(e); }
  };

  const markPayoutPaid = async (bookingId) => {
    try {
      await api.post(`/admin/payouts/${bookingId}/mark-paid`);
      toast.success("Payout marked as paid");
      load();
    } catch (e) { fail(e); }
  };

  const saveClaim = async (data, onDone) => {
    try {
      const payload = { ...data, amount: Number(data.amount || 0) };
      if (data.id) await api.put(`/admin/claims/${data.id}`, { type: payload.type, amount: payload.amount, description: payload.description, charge_source: payload.charge_source, status: payload.status });
      else await api.post("/admin/claims", { booking_id: payload.booking_id, type: payload.type, amount: payload.amount, description: payload.description, charge_source: payload.charge_source });
      toast.success("Claim saved");
      onDone?.();
      load();
    } catch (e) { fail(e); }
  };

  const updateClaim = async (id, patch) => {
    try {
      await api.put(`/admin/claims/${id}`, patch);
      load();
    } catch (e) { fail(e); }
  };

  const deleteClaim = async (id) => {
    if (!window.confirm("Delete this claim?")) return;
    try {
      await api.delete(`/admin/claims/${id}`);
      toast.success("Claim deleted");
      load();
    } catch (e) { fail(e); }
  };

  const saveDiscount = async (data, onDone) => {
    try {
      const payload = { code: data.code, type: data.type, active: data.active, max_uses: data.max_uses === "" || data.max_uses == null ? null : Number(data.max_uses) };
      if (data.id) await api.put(`/admin/discounts/${data.id}`, { active: payload.active, max_uses: payload.max_uses, type: payload.type });
      else await api.post("/admin/discounts", payload);
      toast.success("Discount code saved");
      onDone?.();
      load();
    } catch (e) { fail(e); }
  };

  const toggleDiscount = async (d) => {
    try {
      await api.put(`/admin/discounts/${d.id}`, { active: !d.active });
      load();
    } catch (e) { fail(e); }
  };

  const deleteDiscount = async (id) => {
    if (!window.confirm("Delete this discount code?")) return;
    try {
      await api.delete(`/admin/discounts/${id}`);
      toast.success("Code deleted");
      load();
    } catch (e) { fail(e); }
  };

  return {
    stats, bookings, cars, messages, syncEvents, webhook, discounts,
    hostApplications, payouts, claims, hosts, load,
    updateStatus, resendReceipt, saveCar, deleteCar, toggleAvailability, reviewCar,
    saveDiscount, toggleDiscount, deleteDiscount,
    markPayoutPaid, saveClaim, updateClaim, deleteClaim, markOfflinePaid, stripeTransfer,
  };
}
